/**
 * searchReport — which settled wall query, if any, owes a `wall_search` now. One decision for both
 * readers of the wall: a signed-in reader's settled query is `useWallPages.loadedQuery`, a closed
 * house's visitor's is `useAnonPage.query` (the query its page answers, the server-rendered `?q=`
 * of a deep link included). The effect in `Wall.tsx` supplies the state; this answers.
 */
import type {HouseFacets} from '@core/wall/facets'
import {parseQuery} from '@core/wall/tsQuery'
import {facetValueOf, makeDirection, yoursOf} from './searchModel'

export type SettledSearch = {
    /** The query the tiles on screen answer. */
    query: string
    matchedCount: number
}

/**
 * The search to report, or null. Reported once the page for `typed` has landed (`settled.query`
 * equals it), never for an empty query, never twice for the same query (`firedFor`), and never for
 * the query a restored page set (a Back) had already answered (`restored`).
 */
export function searchToReport(
    typed: string,
    settled: SettledSearch,
    firedFor: string | null,
    restored: string | null,
): SettledSearch | null {
    if (settled.query !== typed || settled.query.trim().length === 0) return null
    if (firedFor === settled.query || restored === settled.query) return null
    return settled
}

/**
 * The direction to arm the ghost with when the field asks to make (the Make row, ⌘/Ctrl+Enter, or Enter at request
 * length), or null: the pills and the typed words, comma-joined (`makeDirection`), at any length. A closed house's
 * visitor has no column to arm (its ghost is the sign-in door), so the press arms nothing there.
 */
export function makeToArm(q: string, facets: HouseFacets, isVisitor: boolean): string | null {
    if (isVisitor) return null
    const parsed = parseQuery(q, (token) => facetValueOf(facets, token) !== null)
    return makeDirection(yoursOf(parsed, facets), parsed.words) || null
}
